import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseUrl } from "../../helpers/constants";
import { getCookie, signout } from "../../helpers/cookies";
import { ordersParser } from "./ordersParser";

const customFetchBaseQuery = fetchBaseQuery({
    baseUrl: baseUrl,
    prepareHeaders: (headers, { getState }) => {
        const token = getCookie("magiclock_admin_token");
        if (token) {
            headers.set("Authorization", `BEARER ${token}`);
        }
        return headers;
    },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
    let result = await customFetchBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 403) {
        signout(() => {
            window.location.href = "/login";
        });
    }

    return result;
};

const ordersApis = createApi({
    reducerPath: "ordersApis",
    baseQuery: baseQueryWithReauth,
    keepUnusedDataFor: 0,
    tagTypes: ["Orders", "OrderDetails", "OrderPayments", "OrderReminders"],
    endpoints: (build) => ({
        // Admin orders
        getAdminOrdersList: build.query({
            query: (params) => ({
                url: "/business/orders/admin/list",
                method: "GET",
                params: params,
            }),
            transformResponse: (response) => ordersParser(response),
            providesTags: ["Orders"],
            keepUnusedDataFor: 5,
        }),
        getAdminOrderDetails: build.query({
            query: (id) => ({
                url: `/business/orders/admin/details/${id}`,
                method: "GET",
            }),
            transformResponse: (response) => response.result?.order || response.result,
            providesTags: (result, error, id) => [{ type: "OrderDetails", id }],
            keepUnusedDataFor: 5,
        }),
        updateOrderStatus: build.mutation({
            query: (payload) => ({
                url: "/business/orders/admin/status",
                method: "PUT",
                body: payload,
            }),
            transformResponse: (response) => response.result,
            invalidatesTags: ["Orders", "OrderDetails"],
        }),
        getOrderPayments: build.query({
            query: (id) => ({
                url: `/business/orders/admin/payments/${id}`,
                method: "GET",
            }),
            transformResponse: (response) => response.result?.payments || response.result,
            providesTags: (result, error, id) => [{ type: "OrderPayments", id }],
            keepUnusedDataFor: 5,
        }),
        getOrderReminders: build.query({
            query: (id) => ({
                url: `/business/orders/admin/reminders/${id}`,
                method: "GET",
            }),
            transformResponse: (response) => response.result?.reminders || response.result,
            providesTags: (result, error, id) => [{ type: "OrderReminders", id }],
            keepUnusedDataFor: 5,
        }),
    }),
});

export const {
    useGetAdminOrdersListQuery,
    useGetAdminOrderDetailsQuery,
    useUpdateOrderStatusMutation,
    useGetOrderPaymentsQuery,
    useGetOrderRemindersQuery,
} = ordersApis;
export default ordersApis;
